/**
 * Version 1 Business Concept Model schema.
 *
 * This is the single generic, vocabulary-agnostic schema shared by built-in
 * presets and custom models (see
 * `specs/999-business-concept-map-admin/data-model.md`). It validates
 * structural correctness only; it does not check whether predicates exist in
 * a particular RDF graph.
 */
import { z } from 'zod';
import { BusinessConceptModelValidationIssue } from './types';

const PREFIX_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_.-]*$/;
// Absolute IRI: a scheme followed by "://" and a non-empty, whitespace-free remainder.
const ABSOLUTE_IRI_PATTERN = /^[A-Za-z][A-Za-z0-9+.-]*:\/\/\S+$/;
// Compact IRI: "<prefixName>:<localName>", where the local name must not itself
// start a "//" authority (which would indicate an absolute IRI instead).
const COMPACT_IRI_PATTERN = /^([A-Za-z_][A-Za-z0-9_.-]*):([^\s/][^\s]*)$/;
// Stable machine name for a relation definition; mirrors the API contract's `NamedBusinessConceptRelation.name` pattern.
const RELATION_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_-]*$/;

const iriString = z.string().min(1, 'IRI must not be empty');
const relationName = z
  .string()
  .regex(RELATION_NAME_PATTERN, 'Invalid relation name');
/** One IRI or a non-empty array of IRIs; a category's admitted type set before compilation normalizes it. */
const categoryTypeShape = z.union([iriString, z.array(iriString).min(1)]);

const prefixesShape = z
  .record(
    z.string().regex(PREFIX_NAME_PATTERN, 'Invalid prefix name'),
    iriString,
  )
  .refine(v => Object.keys(v).length > 0, {
    message: 'At least one prefix is required',
  });

const literalMetadataDefinitionShape = z
  .object({
    predicate: iriString,
    kind: z.literal('literal'),
  })
  .strict();

const hierarchyDefinitionShape = z
  .object({
    childPredicates: z.array(iriString).min(1).optional(),
    parentPredicates: z.array(iriString).min(1).optional(),
  })
  .strict()
  .refine(
    h =>
      (h.childPredicates?.length ?? 0) + (h.parentPredicates?.length ?? 0) > 0,
    {
      message:
        'hierarchy must declare at least one non-empty childPredicates or parentPredicates collection',
    },
  );

const directRelationDefinitionShape = z
  .object({
    kind: z.literal('direct'),
    predicate: iriString,
    label: z.string().min(1),
    'map:reveal': z.enum(['expand', 'manual']).optional(),
    direction: z.enum(['undirected', 'directed']).optional(),
    'map:style': z.enum(['standard', 'dotted']).optional(),
  })
  .strict();

const typedRelationDefinitionShape = z
  .object({
    kind: z.literal('typed'),
    type: iriString,
    sourcePredicate: iriString,
    targetPredicate: iriString,
    labelPredicate: iriString,
    inversePredicate: iriString.optional(),
    'map:style': z.enum(['standard', 'dotted']).optional(),
    'map:reveal': z.enum(['expand', 'manual']).optional(),
  })
  .strict();

const relationDefinitionShape = z.discriminatedUnion('kind', [
  directRelationDefinitionShape,
  typedRelationDefinitionShape,
]);

const metadataMapShape = z.record(
  z.string().min(1),
  literalMetadataDefinitionShape,
);

const conceptDefinitionShape = z
  .object({
    type: categoryTypeShape,
    labelPredicate: iriString,
    descriptionPredicate: iriString,
    synonymsPredicate: iriString.optional(),
    metadata: metadataMapShape.optional(),
    hierarchy: hierarchyDefinitionShape,
    relations: z
      .record(relationName, relationDefinitionShape)
      .refine(relations => Object.keys(relations).length > 0, {
        message: 'At least one relation is required',
      })
      .optional(),
  })
  .strict();

const dataTypeDefinitionShape = z
  .object({
    iri: iriString,
    label: z.string().min(1),
  })
  .strict();

const propertyConceptDefinitionShape = z
  .object({
    type: categoryTypeShape,
    labelPredicate: iriString,
    descriptionPredicate: iriString,
    synonymsPredicate: iriString.optional(),
    sourcePredicate: iriString,
    dataTypePredicate: iriString,
    hierarchy: hierarchyDefinitionShape.optional(),
    dataTypes: z.array(dataTypeDefinitionShape).min(1).optional(),
  })
  .strict();

const rootConceptDefinitionShape = z
  .object({
    type: categoryTypeShape,
    labelPredicate: iriString,
    descriptionPredicate: iriString,
    synonymsPredicate: iriString.optional(),
    metadata: metadataMapShape.optional(),
    memberToRootPredicates: z.array(iriString).min(1),
  })
  .strict();

/** Structural (prefix-unaware) shape of a version 1 Business Concept Model. */
export const businessConceptModelV1ShapeSchema = z
  .object({
    prefixes: prefixesShape,
    concept: conceptDefinitionShape,
    propertyConcept: propertyConceptDefinitionShape.optional(),
    rootConcept: rootConceptDefinitionShape,
  })
  .strict();

export type BusinessConceptModelV1 = z.infer<
  typeof businessConceptModelV1ShapeSchema
>;
export type ConceptDefinition = z.infer<typeof conceptDefinitionShape>;
export type HierarchyDefinition = z.infer<typeof hierarchyDefinitionShape>;
export type LiteralMetadataDefinition = z.infer<
  typeof literalMetadataDefinitionShape
>;
export type DirectRelationDefinition = z.infer<
  typeof directRelationDefinitionShape
>;
export type TypedRelationDefinition = z.infer<
  typeof typedRelationDefinitionShape
>;
export type RelationDefinition = z.infer<typeof relationDefinitionShape>;
export type PropertyConceptDefinition = z.infer<
  typeof propertyConceptDefinitionShape
>;
export type DataTypeDefinition = z.infer<typeof dataTypeDefinitionShape>;
export type RootConceptDefinition = z.infer<typeof rootConceptDefinitionShape>;

/**
 * Resolves a compact or absolute IRI against the model's declared prefixes.
 * Returns `undefined` when the value is malformed or references an unknown prefix.
 */
export function resolveIri(
  prefixes: Record<string, string>,
  value: string,
): string | undefined {
  if (ABSOLUTE_IRI_PATTERN.test(value)) return value;
  const compactMatch = COMPACT_IRI_PATTERN.exec(value);
  if (compactMatch) {
    const [, prefixName, localName] = compactMatch;
    const base = prefixes[prefixName];
    if (base === undefined) return undefined;
    return `${base}${localName}`;
  }
  return undefined;
}

type IriRef = { path: (string | number)[]; value: string };

function collectHierarchyIris(
  hierarchy: HierarchyDefinition,
  basePath: (string | number)[],
): IriRef[] {
  const refs: IriRef[] = [];
  hierarchy.childPredicates?.forEach((v, i) =>
    refs.push({ path: [...basePath, 'childPredicates', i], value: v }),
  );
  hierarchy.parentPredicates?.forEach((v, i) =>
    refs.push({ path: [...basePath, 'parentPredicates', i], value: v }),
  );
  return refs;
}

function collectMetadataIris(
  metadata: Record<string, LiteralMetadataDefinition> | undefined,
  basePath: (string | number)[],
): IriRef[] {
  if (!metadata) return [];
  return Object.entries(metadata).map(([key, def]) => ({
    path: [...basePath, key, 'predicate'],
    value: def.predicate,
  }));
}

function collectRelationIris(
  relations: Record<string, RelationDefinition> | undefined,
  basePath: (string | number)[],
): IriRef[] {
  if (!relations) return [];
  return Object.entries(relations).flatMap(([name, relation]) => {
    const path = [...basePath, name];
    if (relation.kind === 'direct') {
      return [{ path: [...path, 'predicate'], value: relation.predicate }];
    }
    return [
      { path: [...path, 'type'], value: relation.type },
      { path: [...path, 'sourcePredicate'], value: relation.sourcePredicate },
      { path: [...path, 'targetPredicate'], value: relation.targetPredicate },
      { path: [...path, 'labelPredicate'], value: relation.labelPredicate },
      ...(relation.inversePredicate !== undefined
        ? [
            {
              path: [...path, 'inversePredicate'],
              value: relation.inversePredicate,
            },
          ]
        : []),
    ];
  });
}

function collectConceptIris(
  concept: ConceptDefinition,
  basePath: (string | number)[],
): IriRef[] {
  return [
    ...expandCategoryType(concept.type, [...basePath, 'type']),
    { path: [...basePath, 'labelPredicate'], value: concept.labelPredicate },
    {
      path: [...basePath, 'descriptionPredicate'],
      value: concept.descriptionPredicate,
    },
    ...(concept.synonymsPredicate !== undefined
      ? [
          {
            path: [...basePath, 'synonymsPredicate'],
            value: concept.synonymsPredicate,
          },
        ]
      : []),
    ...collectHierarchyIris(concept.hierarchy, [...basePath, 'hierarchy']),
    ...collectMetadataIris(concept.metadata, [...basePath, 'metadata']),
    ...collectRelationIris(concept.relations, [...basePath, 'relations']),
  ];
}

function collectPropertyConceptIris(
  propertyConcept: PropertyConceptDefinition | undefined,
  basePath: (string | number)[],
): IriRef[] {
  if (!propertyConcept) return [];
  const refs: IriRef[] = [
    ...expandCategoryType(propertyConcept.type, [...basePath, 'type']),
    {
      path: [...basePath, 'labelPredicate'],
      value: propertyConcept.labelPredicate,
    },
    {
      path: [...basePath, 'descriptionPredicate'],
      value: propertyConcept.descriptionPredicate,
    },
    ...(propertyConcept.synonymsPredicate !== undefined
      ? [
          {
            path: [...basePath, 'synonymsPredicate'],
            value: propertyConcept.synonymsPredicate,
          },
        ]
      : []),
    {
      path: [...basePath, 'sourcePredicate'],
      value: propertyConcept.sourcePredicate,
    },
    {
      path: [...basePath, 'dataTypePredicate'],
      value: propertyConcept.dataTypePredicate,
    },
  ];
  if (propertyConcept.hierarchy) {
    refs.push(
      ...collectHierarchyIris(propertyConcept.hierarchy, [
        ...basePath,
        'hierarchy',
      ]),
    );
  }
  propertyConcept.dataTypes?.forEach((dt, i) =>
    refs.push({ path: [...basePath, 'dataTypes', i, 'iri'], value: dt.iri }),
  );
  return refs;
}

function collectRootConceptIris(
  rootConcept: RootConceptDefinition | undefined,
  basePath: (string | number)[],
): IriRef[] {
  if (!rootConcept) return [];
  return [
    ...expandCategoryType(rootConcept.type, [...basePath, 'type']),
    {
      path: [...basePath, 'labelPredicate'],
      value: rootConcept.labelPredicate,
    },
    {
      path: [...basePath, 'descriptionPredicate'],
      value: rootConcept.descriptionPredicate,
    },
    ...(rootConcept.synonymsPredicate !== undefined
      ? [
          {
            path: [...basePath, 'synonymsPredicate'],
            value: rootConcept.synonymsPredicate,
          },
        ]
      : []),
    ...collectMetadataIris(rootConcept.metadata, [...basePath, 'metadata']),
    ...rootConcept.memberToRootPredicates.map((v, i) => ({
      path: [...basePath, 'memberToRootPredicates', i],
      value: v,
    })),
  ];
}

function pathToString(path: (string | number)[]): string {
  return path.reduce<string>((acc, segment) => {
    if (typeof segment === 'number') return `${acc}[${segment}]`;
    return acc.length ? `${acc}.${segment}` : segment;
  }, '');
}

type ConceptCategory = 'concept' | 'propertyConcept' | 'rootConcept';

const CONCEPT_CATEGORIES: ConceptCategory[] = [
  'concept',
  'propertyConcept',
  'rootConcept',
];

/** Expands a category's scalar-or-array type declaration into one `IriRef` per member. */
function expandCategoryType(
  value: string | string[],
  basePath: (string | number)[],
): IriRef[] {
  if (Array.isArray(value)) {
    return value.map((v, i) => ({ path: [...basePath, i], value: v }));
  }
  return [{ path: basePath, value }];
}

/**
 * Returns the type IRI(s) declared by a concept category, unresolved.
 */
function getDeclaredTypes(
  data: BusinessConceptModelV1,
  category: ConceptCategory,
): IriRef[] {
  switch (category) {
    case 'concept':
      return expandCategoryType(data.concept.type, ['concept', 'type']);
    case 'propertyConcept':
      return data.propertyConcept
        ? expandCategoryType(data.propertyConcept.type, [
            'propertyConcept',
            'type',
          ])
        : [];
    case 'rootConcept':
      return data.rootConcept
        ? expandCategoryType(data.rootConcept.type, ['rootConcept', 'type'])
        : [];
    default:
      return [];
  }
}

/** Flags resolved-IRI overlaps between the types declared by different concept categories. */
function checkConceptTypeOverlaps(
  data: BusinessConceptModelV1,
): BusinessConceptModelValidationIssue[] {
  const issues: BusinessConceptModelValidationIssue[] = [];
  const seenByResolvedType = new Map<
    string,
    { category: ConceptCategory; path: (string | number)[] }
  >();

  for (const category of CONCEPT_CATEGORIES) {
    for (const { path, value } of getDeclaredTypes(data, category)) {
      const resolved = resolveIri(data.prefixes, value) ?? value;
      const existing = seenByResolvedType.get(resolved);
      if (existing && existing.category !== category) {
        issues.push({
          path: pathToString(path),
          message: `Type '${value}' overlaps with the type declared by '${
            existing.category
          }' at '${pathToString(
            existing.path,
          )}'; concept, rootConcept, and propertyConcept types must be disjoint`,
        });
      } else if (!existing) {
        seenByResolvedType.set(resolved, { category, path });
      }
    }
  }
  return issues;
}

/** Flags resolved-IRI duplicates declared within the same category's plural type list. */
function checkDuplicateTypesWithinCategory(
  data: BusinessConceptModelV1,
): BusinessConceptModelValidationIssue[] {
  const issues: BusinessConceptModelValidationIssue[] = [];
  for (const category of CONCEPT_CATEGORIES) {
    const seen = new Map<string, (string | number)[]>();
    for (const { path, value } of getDeclaredTypes(data, category)) {
      const resolved = resolveIri(data.prefixes, value) ?? value;
      const firstPath = seen.get(resolved);
      if (firstPath) {
        issues.push({
          path: pathToString(path),
          message: `Duplicate type '${value}' (also declared at '${pathToString(
            firstPath,
          )}' once resolved) within the same category`,
        });
      } else {
        seen.set(resolved, path);
      }
    }
  }
  return issues;
}

/** Flags resolved type IRI duplicates between typed relation definitions. */
function checkDuplicateTypedRelationTypes(
  data: BusinessConceptModelV1,
): BusinessConceptModelValidationIssue[] {
  const issues: BusinessConceptModelValidationIssue[] = [];
  const seen = new Map<string, string>();
  Object.entries(data.concept.relations ?? {}).forEach(([name, relation]) => {
    if (relation.kind !== 'typed') return;
    const resolved = resolveIri(data.prefixes, relation.type);
    if (resolved === undefined) return;
    const firstName = seen.get(resolved);
    if (firstName !== undefined) {
      issues.push({
        path: `concept.relations.${name}.type`,
        message: `Duplicate typed relation type '${relation.type}' (also declared at '${firstName}' once resolved)`,
      });
    } else {
      seen.set(resolved, name);
    }
  });
  return issues;
}

export type BusinessConceptModelValidationResult =
  | { success: true; data: BusinessConceptModelV1 }
  | { success: false; issues: BusinessConceptModelValidationIssue[] };

/**
 * Validates a parsed YAML document against the version 1 Business Concept Map
 * Model schema, including compact-IRI prefix resolution and semantic
 * (resolved-IRI) uniqueness of `dataTypes` entries.
 */
export function validateBusinessConceptModel(
  input: unknown,
): BusinessConceptModelValidationResult {
  const structural = businessConceptModelV1ShapeSchema.safeParse(input);
  if (!structural.success) {
    return {
      success: false,
      issues: structural.error.issues.map(issue => ({
        path: pathToString(issue.path),
        message: issue.message,
      })),
    };
  }

  const data = structural.data;
  const prefixNames = Object.keys(data.prefixes);
  const issues: BusinessConceptModelValidationIssue[] = [];

  for (const prefixName of prefixNames) {
    if (!ABSOLUTE_IRI_PATTERN.test(data.prefixes[prefixName])) {
      issues.push({
        path: `prefixes.${prefixName}`,
        message: `Prefix '${prefixName}' must map to an absolute IRI base`,
      });
    }
  }

  const iriRefs: IriRef[] = [
    ...collectConceptIris(data.concept, ['concept']),
    ...collectPropertyConceptIris(data.propertyConcept, ['propertyConcept']),
    ...collectRootConceptIris(data.rootConcept, ['rootConcept']),
  ];

  for (const ref of iriRefs) {
    const resolved = resolveIri(data.prefixes, ref.value);
    if (resolved === undefined) {
      issues.push({
        path: pathToString(ref.path),
        message: `'${ref.value}' is not a valid absolute IRI and does not resolve against a declared prefix`,
      });
    }
  }

  if (data.propertyConcept?.dataTypes) {
    const seen = new Map<string, number>();
    data.propertyConcept.dataTypes.forEach((dt, i) => {
      const resolved = resolveIri(data.prefixes, dt.iri) ?? dt.iri;
      const firstIndex = seen.get(resolved);
      if (firstIndex !== undefined) {
        issues.push({
          path: `propertyConcept.dataTypes[${i}].iri`,
          message: `Duplicate data type IRI (also declared at index ${firstIndex} once resolved)`,
        });
      } else {
        seen.set(resolved, i);
      }
    });
  }

  issues.push(...checkConceptTypeOverlaps(data));
  issues.push(...checkDuplicateTypesWithinCategory(data));
  issues.push(...checkDuplicateTypedRelationTypes(data));

  if (issues.length > 0) return { success: false, issues };
  return { success: true, data };
}
